import React from 'react';
import { Heading, Table, Loader, FormGroup, Alert } from 'arag-ui-lib';
import { DOTS, ERROR, GROUP, HEADING_2, HEADING_3, LABEL, MEDIUM, NOT_AVAILABLE, WARNING } from '../../helpers/constants';
import { FORM_GROUP_VARIANTS } from '../../helpers/formGroupVariants';
import CONTENT_STRINGS from '../../helpers/contentStrings';
import {
    buildFinancialsColumnSet,
    buildFinancialsTableData,
    financialsTableColumns,
    formatMoneyOrNA,
    financialPositionColumns,
    buildOwedRows,
} from '../../helpers/financialsTabFunctions';
import '../../style/table.css';

const FinancialsTab = (props) => {
    const { financials, fetching, error } = props;

    if (fetching) {
        return (
            <div className="col-sm-12">
                <Loader variant={DOTS} dotSize={MEDIUM} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="col-sm-12">
                <Alert label={CONTENT_STRINGS.ERROR_LOADING_FINANCIALS} variant={ERROR} />
            </div>
        );
    }
    if (!financials) {
        return null;
    }

    // Only the columns this policy actually has - see buildFinancialsColumnSet.
    const columnSet = buildFinancialsColumnSet(financials);
    const data = buildFinancialsTableData(columnSet);
    // WI 100878 AC1/AC2: bold the grouping-column headings ("Premium" / "Agent Commission", present only
    // on each group's first row) and the Total/Gross Premium row - visual separators for the rows they
    // head. arag-ui-lib's own Table renders its headers as <strong>, and its FormGroup plain variant is a
    // form-row layout, not something that belongs inside a table cell - so <strong> is the fit here.
    const isBoldFinancialsCell = (row, propertyName) =>
        (propertyName === GROUP && !!row[GROUP]) || row[LABEL] === CONTENT_STRINGS.TOTALS_GROSS_PREMIUM;
    const columns = financialsTableColumns(columnSet).map((column) => ({
        ...column,
        formatter: (row) => (isBoldFinancialsCell(row, column.propertyName)
            ? <strong>{row[column.propertyName]}</strong>
            : row[column.propertyName]),
    }));
    const loiLabel = formatMoneyOrNA(financials.limitOfIndemnity);


    //Section 2 - financialPosition is null when not applicable (non-PLUTUS SoR),
    // so nothing below this point may dereference it without checking first.
    const financialPositionColsOwed = financialPositionColumns(CONTENT_STRINGS.SALES_TYPE, CONTENT_STRINGS.TOTAL_SALES);
    const financialPositionDataOwed = buildOwedRows(financials.financialPosition?.whatWeAreOwed);
    const financialPositionColsOwe = financialPositionColumns(CONTENT_STRINGS.EXPENSES_TYPE, CONTENT_STRINGS.TOTAL_EXPENSES);
    const financialPositionDataOwe = buildOwedRows(financials.financialPosition?.whatWeOwe);

    return (
        <div className="col-sm-12">
            <Heading variant={HEADING_2} title={CONTENT_STRINGS.CURRENT_PREMIUM_INFORMATION} />
            <Table
                columns={columns}
                data={data}
                striped
                responsive
                tableWrapperTestId={'policy-financials-table'}
            />
            <FormGroup
                labelSize={'col-sm-6'}
                inputSize={'col-sm-4'}
                variant={FORM_GROUP_VARIANTS.PLAIN}
                label={CONTENT_STRINGS.LIMIT_OF_INDEMNITY}
                inputProps={{
                    id: 'financials-tab-loi',
                    name: 'financials-tab-loi',
                    label: loiLabel
                }}
                formGroupTestId={'policy-financials-limit-of-indemnity'}
            />
            {
                financials.isPercentagePremium
                &&
                <FormGroup
                    labelSize={'col-sm-6'}
                    inputSize={'col-sm-4'}
                    variant={FORM_GROUP_VARIANTS.PLAIN}
                    label={CONTENT_STRINGS.CURRENT_LC_SPEND}
                    inputProps={{
                        id: 'financials-tab-lc-spend',
                        name: 'financials-tab-lc-spend',
                        label: formatMoneyOrNA(financials.currentLcSpend)
                    }}
                    formGroupTestId={'policy-financials-lc-spend'}
                />
            }
            <FormGroup
                labelSize={'col-sm-6'}
                inputSize={'col-sm-4'}
                variant={FORM_GROUP_VARIANTS.PLAIN}
                label={CONTENT_STRINGS.CURRENT_CASE_STAGE}
                inputProps={{
                    id: 'financials-tab-case-stage',
                    name: 'financials-tab-case-stage',
                    label: financials.currentCaseStage || NOT_AVAILABLE
                }}
                formGroupTestId={'policy-financials-case-stage'}
            />
            <FormGroup
                labelSize={'col-sm-6'}
                inputSize={'col-sm-4'}
                variant={FORM_GROUP_VARIANTS.PLAIN}
                label={CONTENT_STRINGS.CURRENT_DAMAGES_RANGE}
                inputProps={{
                    id: 'financials-tab-damages-range',
                    name: 'financials-tab-damages-range',
                    label: financials.currentDamagesRange || NOT_AVAILABLE
                }}
                formGroupTestId={'policy-financials-damages'}
            />
            <Heading variant={HEADING_2} title={CONTENT_STRINGS.FINANCIAL_POSITION} />
            {
                financials.financialPosition
                ?
                    financials.financialPosition.unavailable
                    ?
                        <Alert variant={WARNING} label={CONTENT_STRINGS.FINANCIAL_POSITION_UNAVAILABLE} />
                    :
                        <>
                            {
                                // AC1: read-only. AC5: hidden entirely when there is no status. Inside this
                                // branch on purpose - a PLUTUS outage renders the warning alert above instead
                                // of a blank status field.
                                financials.financialPosition.paymentStatus
                                ?
                                    <FormGroup
                                        labelSize={'col-sm-6'}
                                        inputSize={'col-sm-4'}
                                        variant={FORM_GROUP_VARIANTS.PLAIN}
                                        label={CONTENT_STRINGS.PAYMENT_STATUS}
                                        inputProps={{
                                            id: 'financials-tab-payment-status',
                                            name: 'financials-tab-payment-status',
                                            label: financials.financialPosition.paymentStatus
                                        }}
                                        formGroupTestId={'policy-financials-payment-status'}
                                    />
                                :
                                    null
                            }
                            {
                                // AC5 names only "What We Are Owed" for the "(excl. Advance Disbursements)"
                                // caveat. This is deliberately asymmetric with What We Owe below, even though
                                // AdvanceDisbursementGateway-purpose ledger entries are actually dropped from BOTH
                                // tables (FinancialPositionBuilder.Build only ever reads Premium/SalesTax/Commission/
                                // PurchaseTax out of byPurpose) - flagged for BA confirmation whether the caveat
                                // should also appear on What We Owe, but following AC5 literally until that's decided.
                            }
                            <Heading variant={HEADING_3} title={CONTENT_STRINGS.WHAT_WE_ARE_OWED} subTitle={CONTENT_STRINGS.EXCL_ADVANCE_DISBURSEMENTS} />
                            <Table
                                columns={financialPositionColsOwed}
                                data={financialPositionDataOwed}
                                striped
                                responsive
                                tableWrapperTestId={'policy-financials-what-we-are-owed-table'}
                            />
                            <Heading variant={HEADING_3} title={CONTENT_STRINGS.WHAT_WE_OWE} />
                            <Table
                                columns={financialPositionColsOwe}
                                data={financialPositionDataOwe}
                                striped
                                responsive
                                tableWrapperTestId={'policy-financials-what-we-owe-table'}
                            />
                        </>
                :
                    null
            }
        </div>
    )
};

export default FinancialsTab;
